import React from "react";
import moment from "moment";
import {Tag} from "antd";
import {useTranslation} from "react-i18next";
import {useStore} from "hooks/useStore";
import {courierStatusColors} from "constants/constants";
import {CourierLink} from "components/_defaultComponents/CourierLink/CourierLink";
import {TableWrapper} from "components/_defaultComponents/TableWrapper/TableWrapper";
import {ResponsiveDataTable} from "components/_defaultComponents/ResponsiveDataTable";
import {CUSTOMER_SHIFTS_PAGE_SIZE} from "../utils/customerShiftsPageUtils";

const formatDate = value => {
    if (!value) return "-";
    return moment(value).format("DD.MM.YYYY");
};

const formatTimeRange = (start, end) => {
    if (!start && !end) return "-";

    const startTime = start ? moment(start).format("HH:mm") : "-";
    const endTime = end ? moment(end).format("HH:mm") : "-";

    return `${startTime} - ${endTime}`;
};

const getRowKey = row =>
    `${row?.courierId || ""}-${row?.forecastDate || ""}-${row?.startTimestamp || ""}`;

const renderCourierStateStatus = (courierState, t) => {
    if (!courierState) {
        return "-";
    }

    return (
        <Tag color={courierStatusColors[courierState] || "default"} style={{margin: 0}}>
            {t(courierState, {
                ns: "courierStatuses",
                defaultValue: courierState
            })}
        </Tag>
    );
};

const DedicatedIntegrationPoolShiftTable = ({
    data = [],
    loading,
    currentPage,
    setCurrentPage,
    dataCount,
    hideStatusColumn = false,
    hideActualShiftColumn = false,
    hideDurationColumn = false,
    hidePackageCountColumn = false
}) => {
    const {t} = useTranslation(["common", "shiftScore", "courierStatuses"]);
    const stores = useStore();

    const totalCount = dataCount ?? data.length;
    const hasData = totalCount > 0;

    return (
        <ResponsiveDataTable
            size="middle"
            pagination={{
                total: totalCount,
                current: currentPage,
                onChange: page => setCurrentPage(page),
                hideOnSinglePage: true,
                showSizeChanger: false,
                pageSize: CUSTOMER_SHIFTS_PAGE_SIZE
            }}
            rowKey={getRowKey}
            dataSource={data}
            data-testid="dedicatedIntegrationPoolShiftTable"
            loading={loading}
            display={hasData ? "grid" : "flex"}
        >
            <TableWrapper.Column
                dataIndex="forecastDate"
                title={t("fields.date", {ns: "common"})}
                render={forecastDate => (
                    <span data-testid="forecastDate">{formatDate(forecastDate)}</span>
                )}
                onCell={() => ({
                    "data-label": t("fields.date", {ns: "common"})
                })}
            />
            <TableWrapper.Column
                dataIndex="courierName"
                title={t("fields.courier", {ns: "common"})}
                render={(courierName, row) => (
                    <span data-testid="courierName">
                        {stores.meStore.checkRole("courierDetail") && row?.courierId ? (
                            <CourierLink target="_blank" to={`/couriers/${row.courierId}`}>
                                {courierName || "-"}
                            </CourierLink>
                        ) : (
                            courierName || "-"
                        )}
                    </span>
                )}
                onCell={() => ({
                    "data-label": t("fields.courier", {ns: "common"})
                })}
            />
            <TableWrapper.Column
                title={t("shiftScore.scheduledShift", {ns: "shiftScore"})}
                render={(_, row) => (
                    <span data-testid="plannedShift">
                        {formatTimeRange(row?.startTimestamp, row?.endTimestamp)}
                    </span>
                )}
                onCell={() => ({
                    "data-label": t("shiftScore.scheduledShift", {ns: "shiftScore"})
                })}
            />
            {!hideActualShiftColumn && (
                <TableWrapper.Column
                    title={t("shiftScore.actualShift", {ns: "shiftScore"})}
                    render={(_, row) => (
                        <span data-testid="actualShift">
                            {formatTimeRange(row?.firstAvailableAt, row?.lastExitAt)}
                        </span>
                    )}
                    onCell={() => ({
                        "data-label": t("shiftScore.actualShift", {ns: "shiftScore"})
                    })}
                />
            )}
            {!hideDurationColumn && (
                <TableWrapper.Column
                    dataIndex="duration"
                    title={t("shiftScore.workTime", {ns: "shiftScore"})}
                    render={duration => <span data-testid="duration">{duration || "-"}</span>}
                    onCell={() => ({
                        "data-label": t("shiftScore.workTime", {ns: "shiftScore"})
                    })}
                />
            )}
            {!hidePackageCountColumn && (
                <TableWrapper.Column
                    dataIndex="deliveredOrderCount"
                    title={t("shiftScore.packageCount", {ns: "shiftScore"})}
                    render={deliveredOrderCount => (
                        <span data-testid="deliveredOrderCount">{deliveredOrderCount ?? "-"}</span>
                    )}
                    onCell={() => ({
                        "data-label": t("shiftScore.packageCount", {ns: "shiftScore"})
                    })}
                />
            )}
            {!hideStatusColumn && (
                <TableWrapper.Column
                    dataIndex="courierState"
                    title={t("fields.status", {ns: "common"})}
                    render={courierState => (
                        <span data-testid="courierState">
                            {renderCourierStateStatus(courierState, t)}
                        </span>
                    )}
                    onCell={() => ({
                        "data-label": t("fields.status", {ns: "common"})
                    })}
                />
            )}
        </ResponsiveDataTable>
    );
};

export default DedicatedIntegrationPoolShiftTable;
